import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';

import { Dialog } from '../../../components/ui/Dialog';
import { useGetImportRulePreviewRecordsQuery } from '../api/parserImportApi';
import { RecordCard } from './RecordCard';

const PAGE_SIZE = 50;
const SKELETON_COUNT = 6;

export const RulePreviewRecordsModal = ({ rule, token, onClose }) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('distance');

  useEffect(() => {
    setPage(1);
  }, [rule?.id, sort]);

  const { data, isLoading, isFetching, error } = useGetImportRulePreviewRecordsQuery(
    {
      token,
      ruleId: rule?.id,
      page,
      per_page: PAGE_SIZE,
      sort,
    },
    { skip: !token || !rule?.id }
  );

  const records = data?.data ?? [];
  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = meta?.last_page ?? 1;
  const gridClass = 'grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  const content = (
    <div
      data-testid="rule-preview-records-modal"
      className="flex flex-col w-[94vw] max-w-7xl max-h-[82vh] text-left"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 pr-10 pb-4 border-b border-gray-200 flex-shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-darkGrayColor uppercase tracking-wider">
            <Eye size={14} className="text-purpleColor" /> Auto-import preview
          </div>
          <h2 id="modal-title" className="mt-1 text-lg font-semibold text-blackColor truncate">
            Preview: {rule?.name}
          </h2>
          <p className="mt-1 text-xs text-darkGrayColor">
            {Number(total).toLocaleString('fr-FR')} records match right now
          </p>
        </div>
        <select
          value={sort}
          onChange={event => setSort(event.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1.5 outline-none focus:border-purpleColor bg-white"
        >
          <option value="distance">Sort: distance</option>
          <option value="price">Sort: price</option>
        </select>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pt-4 pr-1">
        {isLoading && (
          <div className={gridClass}>
            {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <div
                key={index}
                className="w-full h-[300px] rounded-lg border border-gray-200 bg-lightGrayColor animate-pulse"
              />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="flex items-center justify-center h-80 text-sm text-redColor">
            Failed to load matching parser records.
          </div>
        )}

        {!isLoading && !error && records.length === 0 && (
          <div
            data-testid="rule-preview-records-empty"
            className="flex items-center justify-center h-80 text-sm text-darkGrayColor"
          >
            No parser records match this rule right now
          </div>
        )}

        {!isLoading && !error && records.length > 0 && (
          <div className={gridClass}>
            {records.map(record => (
              <div
                key={record.parser_advert_id}
                data-testid={`rule-preview-records-card-${record.parser_advert_id}`}
              >
                <RecordCard record={record} readOnly />
              </div>
            ))}
          </div>
        )}

        {isFetching && !isLoading && (
          <div className="mt-2 text-xs text-darkGrayColor">refreshing…</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4 text-xs flex-shrink-0">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage(current => Math.max(1, current - 1))}
            className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
          >
            <ChevronLeft size={12} /> Prev
          </button>
          <span className="text-darkGrayColor">
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage(current => Math.min(totalPages, current + 1))}
            className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
          >
            Next <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      isOpen={!!rule}
      onClose={onClose}
      classes="w-[96vw] max-w-7xl"
      content={content}
    />
  );
};
